import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// City mapping with coordinates and page routes
const CITY_PAGES = [
  { name: 'Birmingham', route: '/location-birmingham', lat: 52.4862, lon: -1.8904, radius: 30 },
  { name: 'Bristol', route: '/location-bristol', lat: 51.4545, lon: -2.5879, radius: 25 },
  { name: 'Cardiff', route: '/location-cardiff', lat: 51.4816, lon: -3.1791, radius: 25 },
  { name: 'Edinburgh', route: '/location-edinburgh', lat: 55.9533, lon: -3.1883, radius: 30 },
  { name: 'Glasgow', route: '/location-glasgow', lat: 55.8642, lon: -4.2518, radius: 30 },
  { name: 'Leeds', route: '/location-leeds', lat: 53.8008, lon: -1.5491, radius: 25 },
  { name: 'Liverpool', route: '/location-liverpool', lat: 53.4084, lon: -2.9916, radius: 25 },
  { name: 'London', route: '/location-london', lat: 51.5074, lon: -0.1278, radius: 40 },
  { name: 'Manchester', route: '/location-manchester', lat: 53.4808, lon: -2.2426, radius: 30 },
  { name: 'Newcastle', route: '/location-newcastle', lat: 54.9783, lon: -1.6178, radius: 25 },
  { name: 'Newport', route: '/location-newport', lat: 51.5842, lon: -2.9977, radius: 20 },
  { name: 'Swansea', route: '/location-swansea', lat: 51.6214, lon: -3.9436, radius: 20 }
];

// Calculate distance between two coordinates (Haversine formula)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Find nearest city page
function findNearestCity(lat: number, lon: number) {
  for (const city of CITY_PAGES) {
    const distance = calculateDistance(lat, lon, city.lat, city.lon);
    if (distance <= city.radius) {
      return city;
    }
  }
  return null;
}

export default function LocationRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only run on homepage and only once
    if (location.pathname !== '/' || hasChecked) {
      return;
    }

    // Check if user has already been redirected in this session
    const hasBeenRedirected = sessionStorage.getItem('location_redirect_done');
    if (hasBeenRedirected) {
      setHasChecked(true);
      return;
    }

    // Try to get location from IP geolocation API
    const detectLocation = async () => {
      try {
        // Using ipapi.co free API (no key required, 1000 requests/day)
        const response = await fetch('https://ipapi.co/json/');
        
        if (!response.ok) {
          throw new Error('Geolocation API failed');
        }

        const data = await response.json();
        
        if (data.latitude && data.longitude) {
          const nearestCity = findNearestCity(data.latitude, data.longitude);
          
          if (nearestCity) {
            // Mark as redirected for this session
            sessionStorage.setItem('location_redirect_done', 'true');
            
            // Redirect to city page
            navigate(nearestCity.route, { replace: true });
          } else {
            // No city page available, stay on homepage
            sessionStorage.setItem('location_redirect_done', 'true');
          }
        }
      } catch (error) {
        console.log('Location detection skipped:', error);
        // Silently fail - user stays on homepage
      } finally {
        setHasChecked(true);
      }
    };

    detectLocation();
  }, [location.pathname, navigate, hasChecked]);

  // This component doesn't render anything
  return null;
}
